// Gravel project math: area × depth → cubic yards, cubic feet, tons, bags.
// Density varies by gravel type (lbs per cubic ft).
import { toFeet, rectArea, applyWaste } from '../lib/calc'
import type { UnitSystem } from '../lib/calc'
import { ceilTo, round } from '../lib/format'
import type { AccessoryLine } from '../data/companions'
import type { Locale } from '../i18n/locale'

export interface GravelArea {
  length: number
  width: number
}

export type GravelType = 'pea-gravel' | 'crushed-stone' | 'river-rock' | 'decomposed-granite'

// 'dimensions' = user enters one or more L × W areas (current default).
// 'area' = user already knows the total area and types it directly.
export type GravelInputMode = 'dimensions' | 'area'

export interface GravelInputs {
  unit: UnitSystem
  inputMode: GravelInputMode
  areas: GravelArea[]
  totalArea: number      // sq ft (imperial) or m² (metric) — used in 'area' mode
  depthIn: number        // depth in inches, default 3
  gravelType: GravelType
  wastePct: number       // 5-15, default 10
  needFabric: boolean
  needEdging: boolean
  needCompactor: boolean  // plate compactor rental (for crushed stone/DG)
}

export interface GravelResult {
  totalArea: number             // sq ft
  totalAreaM2: number           // m² (metric mirror)
  cubicFeet: number             // before waste
  cubicFeetWithWaste: number    // after waste (imperial volume display)
  cubicYards: number            // after waste (bulk-delivery convenience figure)
  cubicMeters: number           // after waste (metric volume display)
  tons: number                  // after waste
  bags50lb: number              // 50 lb bags equivalent
  fabricRolls: number
  edgingRolls: number
  lines: AccessoryLine[]
}

// lbs per cubic foot by gravel type
const DENSITY: Record<GravelType, number> = {
  'pea-gravel': 105,
  'crushed-stone': 100,
  'river-rock': 90,
  'decomposed-granite': 100,
}

export const GRAVEL_TYPE_LABELS: Record<GravelType, string> = {
  'pea-gravel': 'Pea gravel (105 lbs/cu ft)',
  'crushed-stone': 'Crushed stone (100 lbs/cu ft)',
  'river-rock': 'River rock (90 lbs/cu ft)',
  'decomposed-granite': 'Decomposed granite (100 lbs/cu ft)',
}

const FABRIC_SQFT_PER_ROLL = 150    // 3 × 50 ft
const EDGING_ROLL_FT = 20
const BAG_LBS = 50

export const DEFAULT_GRAVEL: GravelInputs = {
  unit: 'imperial',
  inputMode: 'dimensions',
  areas: [{ length: 10, width: 10 }],
  totalArea: 100,
  depthIn: 3,
  gravelType: 'pea-gravel',
  wastePct: 10,
  needFabric: false,
  needEdging: false,
  needCompactor: false,
}

export function computeGravel(i: GravelInputs, locale: Locale = 'en'): GravelResult {
  const tr = (en: string, fr: string) => (locale === 'fr' ? fr : en)
  let totalArea = 0
  let perimeterFt = 0

  if (i.inputMode === 'area') {
    // Direct area entry — convert m² → sq ft if the user is in metric mode.
    // Perimeter is unknown, so the auto edging estimate falls back to 0.
    totalArea = i.unit === 'metric' ? Math.max(0, i.totalArea) * 10.7639104 : Math.max(0, i.totalArea)
    perimeterFt = 0
  } else {
    for (const a of i.areas ?? []) {
      const lFt = toFeet(a.length, i.unit)
      const wFt = toFeet(a.width, i.unit)
      totalArea += rectArea(lFt, wFt)
      perimeterFt += 2 * (Math.max(0, lFt) + Math.max(0, wFt))
    }
  }

  const depthFt = Math.max(0, i.depthIn) / 12
  const cubicFeet = totalArea * depthFt
  const cubicFeetWithWaste = applyWaste(cubicFeet, i.wastePct)
  const cubicYards = cubicFeetWithWaste / 27

  const density = DENSITY[i.gravelType] ?? 100
  const totalLbs = cubicFeetWithWaste * density
  const tons = totalLbs / 2000

  const bags50lb = totalArea > 0 ? ceilTo(totalLbs / BAG_LBS, 1) : 0

  const fabricRolls = i.needFabric && totalArea > 0
    ? Math.max(1, ceilTo(totalArea / FABRIC_SQFT_PER_ROLL, 1))
    : 0

  const edgingRolls = i.needEdging && perimeterFt > 0
    ? Math.max(1, ceilTo(perimeterFt / EDGING_ROLL_FT, 1))
    : 0

  const lines: AccessoryLine[] = []

  if (i.needFabric) {
    lines.push({
      item: 'landscape fabric weed barrier',
      label: tr('Landscape fabric', 'Membrane géotextile'),
      kind: 'consumable',
      qty: fabricRolls,
      unit: 'roll',
      note: tr(`3 × 50 ft rolls (${FABRIC_SQFT_PER_ROLL} sq ft) · overlap seams 6 in · gravel over fabric prevents weed growth`, `Rouleaux de 3 × 50 pi (${FABRIC_SQFT_PER_ROLL} pi²) · chevaucher les joints de 6 po · le gravier sur la membrane empêche la pousse des mauvaises herbes`),
    })
  } else {
    lines.push({
      item: 'landscape fabric weed barrier',
      label: tr('Landscape fabric', 'Membrane géotextile'),
      kind: 'consumable',
      qty: 0,
      unit: 'roll',
      note: tr('Check the option above to add landscape fabric — recommended under all gravel installs', 'Cochez l’option ci-dessus pour ajouter la membrane géotextile — recommandée sous toute pose de gravier'),
      optional: true,
    })
  }

  if (i.needEdging) {
    lines.push({
      item: 'metal plastic landscape edging border',
      label: tr('Landscape edging', 'Bordure de paysagement'),
      kind: 'consumable',
      qty: edgingRolls,
      unit: 'roll',
      note: tr(`${EDGING_ROLL_FT} ft rolls · steel edging holds gravel in place and creates a clean border`, `Rouleaux de ${EDGING_ROLL_FT} pi · la bordure d’acier retient le gravier en place et crée une bordure nette`),
    })
  } else {
    lines.push({
      item: 'metal plastic landscape edging border',
      label: tr('Landscape edging', 'Bordure de paysagement'),
      kind: 'consumable',
      qty: 0,
      unit: 'roll',
      note: tr('Edging keeps gravel from spreading into grass — check the option above to add it', 'La bordure empêche le gravier de s’étendre dans le gazon — cochez l’option ci-dessus pour l’ajouter'),
      optional: true,
    })
  }

  if (i.gravelType === 'crushed-stone' || i.gravelType === 'decomposed-granite') {
    lines.push({
      item: 'plate compactor rental tool',
      label: tr('Plate compactor', 'Plaque vibrante'),
      kind: 'tool',
      note: tr('Rent from big-box or equipment rental. Compacting DG and crushed stone is essential for driveways and paths.', 'À louer chez un grand détaillant ou un centre de location d’équipement. Le compactage du granit décomposé et de la pierre concassée est essentiel pour les entrées et les sentiers.'),
      optional: !i.needCompactor,
    })
  }

  lines.push({ item: 'landscape rake gravel spreading', label: tr('Landscape rake', 'Râteau de paysagement'), kind: 'tool', note: tr('Wide-tine rake for spreading gravel to a level depth.', 'Râteau à dents larges pour étaler le gravier à une profondeur uniforme.') })
  lines.push({ item: 'garden wheelbarrow', label: tr('Wheelbarrow', 'Brouette'), kind: 'tool', optional: true })
  lines.push({ item: 'landscape fabric staples pins', label: tr('Landscape staples', 'Agrafes de paysagement'), kind: 'consumable', qty: fabricRolls > 0 ? Math.max(1, ceilTo(totalArea / 4, 1)) : 0, unit: 'staple', note: tr('Pin fabric every 2 sq ft and along seams — optional without fabric', 'Fixez la membrane tous les 2 pi² et le long des joints — facultatif sans membrane'), optional: !i.needFabric })

  // Metric mirrors. 1 sq ft = 0.09290304 m²; 1 cu ft = 0.028316846592 m³.
  const totalAreaM2 = totalArea * 0.09290304
  const cubicMeters = cubicFeetWithWaste * 0.028316846592

  return {
    totalArea: round(totalArea, 0),
    totalAreaM2: round(totalAreaM2, 1),
    cubicFeet: round(cubicFeet, 1),
    cubicFeetWithWaste: round(cubicFeetWithWaste, 1),
    cubicYards: round(cubicYards, 2),
    cubicMeters: round(cubicMeters, 2),
    tons: round(tons, 2),
    bags50lb,
    fabricRolls,
    edgingRolls,
    lines,
  }
}
